import { useTranslation } from 'react-i18next';
import { Chunk, DROP_REASONS } from '@quizdb/common';
import { formatMicroUsd } from './formatCost';
import { ChunkStatusPill } from './JobBadges';

/** Props for `JobChunkTable`. */
export interface JobChunkTableProps {
  chunks: readonly Chunk[];
}

/**
 * A job's chunks in plan order, with their status, drafts, drops and cost (QZ-D49, QZ-D56). A
 * chunk only reports drafts and drops once it is `done` or `failed`; a running chunk's error,
 * if any, is shown under its sub-topic.
 */
export const JobChunkTable = ({ chunks }: JobChunkTableProps) => {
  const { t } = useTranslation();

  return (
    <table className="table align-middle qz-small qz-table-stack">
      <thead>
        <tr>
          <th>{t('job.field.chunk')}</th>
          <th>{t('task.filter.status')}</th>
          <th className="text-end">{t('job.field.drafts')}</th>
          <th className="text-end">{t('job.field.dropped')}</th>
          <th className="text-end">{t('job.field.cost')}</th>
        </tr>
      </thead>
      <tbody>
        {chunks.map((chunk, index) => {
          const finished = chunk.status === 'done' || chunk.status === 'failed';
          const droppedText = finished
            ? DROP_REASONS.filter((reason) => chunk.dropped[reason] > 0)
                .map((reason) => t(`job.drop.${reason}`, { count: chunk.dropped[reason] }))
                .join(', ') || '0'
            : '—';
          const costText =
            chunk.status === 'queued' && chunk.microUsd === 0
              ? '—'
              : formatMicroUsd(chunk.microUsd);

          return (
            <tr key={index}>
              <td>
                {`${index + 1} · ${chunk.subTopic}`}
                {chunk.error && (chunk.status === 'failed' || chunk.status === 'running') && (
                  <div className="qz-small qz-muted">{chunk.error}</div>
                )}
              </td>
              <td>
                <ChunkStatusPill chunk={chunk} />
              </td>
              <td className="text-end" data-label={t('job.field.drafts')}>
                {finished ? chunk.drafted : '—'}
                {finished && chunk.approved > 0 && (
                  <div className="qz-small qz-muted">
                    {t('job.chunk.approved', { count: chunk.approved })}
                  </div>
                )}
              </td>
              <td className="text-end" data-label={t('job.field.dropped')}>
                {droppedText}
              </td>
              <td className="text-end qz-mono" data-label={t('job.field.cost')}>
                {costText}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
